/**
 * CME trading-day math for bar bucketing (SPEC §10.4, §12.3 "Daily bars follow the CME trading
 * day"). A TypeScript port of the pieces of `crates/domain/src/risk/calendar.rs` (and
 * `crates/domain/src/calendar.rs`) that daily/weekly/monthly bars and the per-session reset of
 * trade-built bars need; the tests re-use the Rust tests' DST instants.
 *
 * - The trading day runs 17:00 CT to 16:00 CT the next day and is **named by the CT calendar date
 *   on which it closes**: the session that opens Sunday 17:00 CT is Monday's. Timestamps at or
 *   after 17:00 CT belong to the next day; Saturday and Sunday roll forward to Monday. The
 *   16:00–17:00 maintenance break belongs to the day that just closed.
 * - US Central time uses the rule in force since 2007 (CDT from the second Sunday of March 02:00
 *   local to the first Sunday of November 02:00 local). No tz database: pure integer math on UTC
 *   epoch milliseconds, identical in the worker, node tests and the Rust domain crate.
 * - Exchange holidays and early closes are not modelled (as in the Rust calendar): a holiday looks
 *   like a trading day with no trades, so it simply produces no daily bar.
 *
 * "Day ids" are days since 1970-01-01 of a civil date (proleptic Gregorian).
 *
 * Owner decisions 2026-09-27 (ADR-0015): intraday time bars count from the session open
 * (`sessionBucket`, Q-T3), and N-day / N-week bars count trading days / weeks from a fixed epoch
 * Monday (`tradingDayIndex`, `weekIndex`, Q-T4).
 */

const DAY = 86_400_000;
const HOUR = 3_600_000;
const CST = -6 * HOUR;
const CDT = -5 * HOUR;
/** Session open (reset) and close, minutes after CT midnight. */
export const SESSION_OPEN_MINUTE = 17 * 60;
export const SESSION_CLOSE_MINUTE = 16 * 60;

/** Days since 1970-01-01 of a civil date (H. Hinnant's `days_from_civil`); `m` is 1–12. */
export function daysFromCivil(y: number, m: number, d: number): number {
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (m > 2 ? m - 3 : m + 9) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146_097 + doe - 719_468;
}

/** Inverse of `daysFromCivil`: `[year, month 1–12, day]`. */
export function civilFromDays(days: number): [number, number, number] {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const doe = z - era * 146_097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1_460) + Math.floor(doe / 36_524) - Math.floor(doe / 146_096)) / 365,
  );
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  return [yoe + era * 400 + (m <= 2 ? 1 : 0), m, d];
}

/** 0 = Sunday … 6 = Saturday (1970-01-01 was a Thursday). */
export function weekday(days: number): number {
  return (((days + 4) % 7) + 7) % 7;
}

function nthSunday(y: number, m: number, n: number): number {
  const first = daysFromCivil(y, m, 1);
  return first + ((7 - weekday(first)) % 7) + 7 * (n - 1);
}

/** UTC ms at which CDT starts: second Sunday of March, 02:00 CST = 08:00 UTC. */
export function dstStartMs(year: number): number {
  return nthSunday(year, 3, 2) * DAY + 2 * HOUR - CST;
}

/** UTC ms at which CDT ends: first Sunday of November, 02:00 CDT = 07:00 UTC. */
export function dstEndMs(year: number): number {
  return nthSunday(year, 11, 1) * DAY + 2 * HOUR - CDT;
}

/** Offset of US Central time from UTC at a UTC instant: −5 h (CDT) or −6 h (CST), in ms. */
export function centralOffsetMs(utcMs: number): number {
  const [year] = civilFromDays(Math.floor(utcMs / DAY));
  return utcMs >= dstStartMs(year) && utcMs < dstEndMs(year) ? CDT : CST;
}

/**
 * Central civil day + minute of day → UTC ms. The nonexistent spring-forward hour resolves one
 * hour forward; the repeated fall-back hour resolves to the earlier (CDT) instant. Neither can
 * arise with the 16:00 / 17:00 CME boundaries.
 */
export function centralToUtcMs(day: number, minuteOfDay: number): number {
  const local = day * DAY + minuteOfDay * 60_000;
  const asCdt = local - CDT;
  return centralOffsetMs(asCdt) === CDT ? asCdt : local - CST;
}

/** Trading day (day id of its CT close date) containing a UTC instant. */
export function tradingDayOf(utcMs: number): number {
  const local = utcMs + centralOffsetMs(utcMs);
  let day = Math.floor(local / DAY);
  if (local - day * DAY >= SESSION_OPEN_MINUTE * 60_000) day += 1;
  const wd = weekday(day);
  if (wd === 6) day += 2;
  else if (wd === 0) day += 1;
  return day;
}

/** Open (17:00 CT the calendar day before, so Sunday for Monday) of a trading day, UTC ms. */
export function tradingDayOpenMs(day: number): number {
  return centralToUtcMs(day - 1, SESSION_OPEN_MINUTE);
}

/** Close (16:00 CT on the day itself) of a trading day, UTC ms. */
export function tradingDayCloseMs(day: number): number {
  return centralToUtcMs(day, SESSION_CLOSE_MINUTE);
}

/**
 * Monday's trading day of the week containing trading day `day` (weeks run Sunday 17:00 CT to
 * Friday 16:00 CT, i.e. Monday–Friday trading days).
 */
export function weekOf(day: number): number {
  return day - ((weekday(day) + 6) % 7);
}

/** Months since January 1970 of a trading day's (close) date. */
export function monthOf(day: number): number {
  const [y, m] = civilFromDays(day);
  return (y - 1970) * 12 + (m - 1);
}

/** First weekday (Mon–Fri) of a month index from `monthOf`. */
export function firstTradingDayOfMonth(month: number): number {
  const y = 1970 + Math.floor(month / 12);
  const m = month - (y - 1970) * 12 + 1;
  let d = daysFromCivil(y, m, 1);
  while (weekday(d) === 0 || weekday(d) === 6) d++;
  return d;
}

/** Last weekday (Mon–Fri) of a month index from `monthOf`. */
export function lastTradingDayOfMonth(month: number): number {
  let d = firstTradingDayOfMonth(month + 1) - 1;
  while (weekday(d) === 0 || weekday(d) === 6) d--;
  return d;
}

/**
 * Epoch of N-day and N-week bar counting (owner decision 2026-09-27, Q-T4): Monday 1970-01-05
 * (day id 4), the first Monday of the epoch. Bars group trading days / weeks counted from here, so
 * a bar's boundaries never depend on where the loaded history starts.
 */
export const EPOCH_MONDAY = 4;

/**
 * Trading days (Monday–Friday) from `EPOCH_MONDAY` to trading day `day` (a weekday). Holidays are
 * not modelled, so they count as days: a 2D bar spanning one holds one day of trades.
 */
export function tradingDayIndex(day: number): number {
  const d = day - EPOCH_MONDAY;
  const w = Math.floor(d / 7);
  return w * 5 + Math.min(d - w * 7, 4);
}

/** Inverse of `tradingDayIndex`: the trading day (day id) with that index. */
export function tradingDayAt(index: number): number {
  const w = Math.floor(index / 5);
  return EPOCH_MONDAY + w * 7 + (index - w * 5);
}

/** Trading weeks from the epoch week (Monday `EPOCH_MONDAY`) to the week holding trading day `day`. */
export function weekIndex(day: number): number {
  return Math.floor((weekOf(day) - EPOCH_MONDAY) / 7);
}

/**
 * Remembers the current trading day's `[previous reset, own reset)` window so the per-trade hot path
 * only recomputes the calendar when a trade crosses a session boundary. `open` is the day's 17:00 CT
 * open (Sunday for Monday; the weekend before it still belongs to Monday's window), `reset` the next
 * day's open.
 */
export class TradingDayCache {
  private lo = Infinity;
  private hi = -Infinity;
  private day = 0;
  /** 17:00 CT open of the last day returned by `of`. */
  open = Number.NaN;
  /** Exclusive end of that day's window (its own 17:00 CT reset). */
  reset = Number.NaN;

  /** Trading day of `utcMs` (the maintenance break and weekend belong to the day that closed). */
  of(utcMs: number): number {
    if (utcMs >= this.lo && utcMs < this.hi) return this.day;
    const day = tradingDayOf(utcMs);
    this.day = day;
    // A trading day owns [previous reset, its own reset): the weekend rolls forward, so Friday's
    // day ends at Friday 17:00 CT and Monday's starts there.
    this.lo = centralToUtcMs(day - (weekday(day) === 1 ? 3 : 1), SESSION_OPEN_MINUTE);
    this.hi = centralToUtcMs(day, SESSION_OPEN_MINUTE);
    this.open = tradingDayOpenMs(day);
    this.reset = this.hi;
    return day;
  }

  /** End of the bucket last returned by `bucketOpen`. */
  bucketEnd = Number.NaN;

  /** `sessionBucket` without allocating (the per-trade path): returns the open, sets `bucketEnd`. */
  bucketOpen(utcMs: number, periodMs: number): number {
    this.of(utcMs);
    const open = this.open + Math.floor((utcMs - this.open) / periodMs) * periodMs;
    this.bucketEnd = Math.min(open + periodMs, this.reset);
    return open;
  }
}

/**
 * Session-aligned intraday bucket (owner decision 2026-09-27, Q-T3): time bars under a day count
 * whole periods from the trading day's 17:00 CT open, so 4h bars open at 17:00, 21:00, 01:00, 05:00,
 * 09:00 and 13:00 CT in both CST and CDT (DST changes fall on the weekend, never inside a session).
 * A period that divides an hour lands on the usual clock marks (5m at :00, :05 …); odd periods
 * (7m, 90s, 5h) restart at every session open. Returns `[bucket open, bucket end)` in UTC ms; the
 * end is cut at the day's reset, so the last bucket of a session never reaches into the next one.
 */
export function sessionBucket(days: TradingDayCache, utcMs: number, periodMs: number): [number, number] {
  const open = days.bucketOpen(utcMs, periodMs);
  return [open, days.bucketEnd];
}
